import React, { useState, useEffect } from 'react';
import { signInWithRedirect, getRedirectResult, signInWithEmailAndPassword, createUserWithEmailAndPassword, onAuthStateChanged, updateProfile } from 'firebase/auth';
import { auth, createAdminUserIfNeeded } from '../../services/firebase';
import { StaffUser } from '../../types';
import { googleProvider } from '../../config/firebase';
import AdminLogin from './AdminLogin';
import AdminSignup from './AdminSignup';
import AdminDashboard from './AdminDashboard';

interface AdminLayoutProps {
  // Add any props if needed for admin configuration
}

const AdminLayout: React.FC<AdminLayoutProps> = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleLogin = async (email: string, password: string) => {
    setIsLoading(true);
    setError(undefined);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('User signed in:', userCredential.user);
      setIsAuthenticated(true);
      localStorage.setItem('adminAuthenticated', 'true');
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('An error occurred during login. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(undefined);

    try {
      // Use redirect instead of popup to avoid COOP issues
      await signInWithRedirect(auth, googleProvider);
      // User will be redirected to Google, then back to the app
      // The redirect result will be handled in useEffect
    } catch (err: any) {
      console.error('Google sign-in error:', err);
      setError('An error occurred during Google sign-in. Please try again.');
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setShowSignup(false);
    localStorage.removeItem('adminAuthenticated');
    // Additional cleanup if needed
  };

  const handleGoToSignup = () => {
    setShowSignup(true);
    setError(undefined);
  };

  const handleBackToLogin = () => {
    setShowSignup(false);
    setError(undefined);
  };

  const handleSignup = async (email: string, password: string, confirmPassword: string, fullName: string) => {
    setIsLoading(true);
    setError(undefined);

    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('User account created:', user);
      
      // Update user profile with the full name
      await updateProfile(user, { displayName: fullName });
      
      // Create staff user record in Firestore
      try {
        // Split full name into first and last name
        const nameParts = fullName.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        // Create new staff user with admin role
        const newStaffUser: Partial<StaffUser> = {
          uid: user.uid,
          email: user.email || '',
          displayName: fullName,
          firstName: firstName,
          lastName: lastName,
          role: 'admin', // First user is always admin
          photoURL: user.photoURL || undefined,
          isActive: true
        };
        
        await createAdminUserIfNeeded(user);
        console.log('Staff user record created in Firestore');
      } catch (staffErr) {
        console.error('Error creating staff user record:', staffErr);
        // Still allow login even if staff record creation fails
      }
      
      setIsAuthenticated(true);
      setShowSignup(false);
      localStorage.setItem('adminAuthenticated', 'true');
    } catch (err: any) {
      console.error('Signup error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('An error occurred during signup. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Check for existing authentication on component mount
  useEffect(() => {
    // Handle redirect result from Google sign-in
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result && result.user) {
          console.log('Google sign-in successful via redirect:', result.user);
          
          // Create admin user record if it doesn't exist
          try {
            const staffUser = await createAdminUserIfNeeded(result.user);
            console.log('Staff user record created/updated:', staffUser);
          } catch (staffErr) {
            console.error('Error creating staff user record:', staffErr);
            // Still allow login even if staff record creation fails
          }
          
          setIsAuthenticated(true);
          localStorage.setItem('adminAuthenticated', 'true');
        }
      } catch (err: any) {
        console.error('Error handling redirect result:', err);
        if (err.code !== 'auth/popup-closed-by-user') {
          setError('An error occurred during Google sign-in. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    handleRedirectResult();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setIsAuthenticated(true);
        localStorage.setItem('adminAuthenticated', 'true');
        
        // Create admin user record if it doesn't exist
        try {
          await createAdminUserIfNeeded(user);
        } catch (err) {
          console.error('Error creating staff user record:', err);
        }
      } else {
        setIsAuthenticated(false);
        localStorage.removeItem('adminAuthenticated');
      }
    });
    
    return () => unsubscribe();
  }, []);

  if (isAuthenticated) {
    return <AdminDashboard onLogout={handleLogout} />;
  }

  if (showSignup) {
    return (
      <AdminSignup 
        onSignup={handleSignup}
        onGoogleSignIn={handleGoogleSignIn}
        onBackToLogin={handleBackToLogin}
        isLoading={isLoading}
        error={error}
      />
    );
  }

  return (
    <AdminLogin 
      onLogin={handleLogin}
      onGoogleSignIn={handleGoogleSignIn}
      onGoToSignup={handleGoToSignup}
      isLoading={isLoading}
      error={error}
    />
  );
};

export default AdminLayout;
